import React, { useState } from 'react';
import { API_URL } from '../../utils/constants';
import auth from '../../Auth';

const GettingStarted = () => {
  const [newToken, setNewToken] = useState('');
  const [showModal, setShowModal] = useState(false); // To control modal visibility
  const requestNewToken = () => {
    fetch(`${API_URL}/api_tokens`, {
      method: 'POST',
      headers: {
        Authorization: 'Bearer ' + auth.syncGetIdToken(),
        'Content-Type': 'application/json'
      }
    })
      .then((response) => response.json())
      .then((data) => {
        setNewToken(data.api_token);
        setShowModal(true);
      })
      .catch((error) => console.error('Error requesting new token:', error));
  };

  const copyTokenToClipboard = () => {
    navigator.clipboard
      .writeText(newToken)
      .then(() => {
        console.log('Token copied to clipboard');
      })
      .catch((error) => {
        console.error('Error copying token to clipboard:', error);
      });
  };

  return (
    <div className='bg-white text-black min-h-screen p-8'>
      <div className='max-w-2xl mx-auto'>
        <h1 className='text-3xl font-bold mb-2'>Get started</h1>
        <p className='mb-4'>
          The best way to get started with Lamoom is to run your first eval with starter code. You can also{' '}
          <a href='/prompt/new/' className='text-blue-400 hover:underline'>
            create a project
          </a>{' '}
          from scratch via the UI. Choose your weapon, and let's dive in.
        </p>
        {/* Package Manager Selection */}
        <div className='mb-4'>
          <p className='mb-2'>If you want to use it from your code then Install Lamoom using pip</p>
          <div className='bg-white text-black border border-black p-3 rounded font-mono text-sm mb-6'>
            <span className='text-purple-400'>pip</span> <span className='text-blue-400'>install</span> lamoom
          </div>
        </div>
        {/* Tutorial File */}
        <div className='mb-6'>
          <p className='mb-2'>
            Add <span className='text-blue-400 font-mono'>Keys</span> depending on models you're using
          </p>
          <div className='bg-white text-black border border-black p-4 rounded font-mono text-sm whitespace-pre-wrap overflow-x-auto'>
            <div className='rounded-md overflow-auto max-w-3xl'>
              <pre className='text-sm font-mono'>
                <span className='text-gray-500'># Import core libraries and load environment variables</span>
                <br />
                <span className='text-green-600'>from</span> <span className='text-blue-600'>lamoom </span>
                <span className='text-green-600'>import</span> <span className='text-blue-600'>Lamoom, Prompt</span>
                <br />
                <br />
                <span className='text-blue-400'>api_token</span> = <span className='text-green-400'>YOUR_LAMOOM_API_TOKEN</span>
                <br />
                <span className='text-blue-400'>openai_key</span> = <span className='text-green-400'>YOUR_OPENAI_API_KEY</span>
              </pre>
            </div>
          </div>
        </div>
        <div className='mb-6'>
          <p className='mb-2'>
            Creating <span className='text-blue-400 font-mono'>Your</span> First Prompt
          </p>
          <div className='bg-white text-black border border-black p-4 rounded font-mono text-sm whitespace-pre-wrap overflow-x-auto'>
            <pre className='text-sm font-mono'>
              <span className='text-gray-500'># Create a prompt with unique ID</span>
              <br />
              <span className='text-blue-400'>agent</span> = <span className='text-purple-600'>Prompt</span>(
              <span className='text-green-400'>"answer_on_medical_question"</span>)
              <br />
              <span className='text-gray-500'># Add instructions with a dynamic variable for the question</span>
              <br />
              <span className='text-blue-400'>agent</span>.<span className='text-purple-600'>add</span>(
              <span className='text-green-400'>"""Answer on my question:</span>
              <br />
              <span className='text-green-400'>&#123;question&#125;</span>
              <br />
              <span className='text-green-400'>Please first think out loud before answering.</span>
              <br />
              <span className='text-green-400'>"""</span>, <span className='text-blue-400'>role</span>=
              <span className='text-green-400'>"system"</span>)
            </pre>
          </div>
        </div>
        <div className='mb-6'>
          <p className='mb-2'>
            Call <span className='text-blue-400 font-mono'>Your</span> Prompt
          </p>
          <div className='bg-white text-black border border-black p-4 rounded font-mono text-sm whitespace-pre-wrap overflow-x-auto'>
            <pre className='text-sm font-mono'>
              <span className='text-gray-500'># initialize the Lamoom client</span>
              <br />
              <span className='text-blue-400'>client</span> = <span className='text-purple-600'>Lamoom</span>(
              <span className='text-blue-400'>openai_key</span>=<span className='text-blue-400'>openai_key</span>,
              <span className='text-blue-400'> api_token</span>=<span className='text-blue-400'>api_token</span>)
              <br />
              <span className='text-gray-500'># Test Lamoom with a simple prompt</span>
              <br />
              <span className='text-blue-400'>question</span> = <span className='text-green-400'>"I've been having trouble sleeping. Any suggestions?"</span>
              <br />
              <span className='text-blue-400'>ideal_answer</span> = <span className='text-green-400'>'Insomnia can be caused by stress, anxiety, depression, or medical conditions like sleep apnea. Establishing a regular sleep schedule, avoiding caffeine and alcohol before bed, and creating a relaxing bedtime routine can help. If insomnia persists, consult a doctor for possible treatments'</span>
              <br />
              <span className='text-blue-400'>context</span> = &#123;
              <br />
              &nbsp;&nbsp;<span className='text-green-400'>'question'</span>: <span className='text-blue-400'>question</span>
              <br />
              &#125;
              <br />
              <span className='text-blue-400'>response</span> = <span className='text-blue-400'>client</span>.
              <span className='text-purple-600'>call</span>(
              <br />
              &nbsp;&nbsp;<span className='text-blue-400'>agent</span>.<span className='text-blue-400'>id</span>,{' '}
              
              <br />
              &nbsp;&nbsp;<span className='text-blue-400'>context</span>,{' '}
              <span className='text-gray-500'># Variables to inject into the prompt</span>
              <br />
              &nbsp;&nbsp;<span className='text-green-400'>'openai/o3-mini'</span>,{' '}
              <span className='text-gray-500'># The model to use</span>
              <br />
              &nbsp;&nbsp;<span className='text-blue-400'>test_data</span> = &#123;
              <br />
              &nbsp;&nbsp;&nbsp;&nbsp;<span className='text-green-400'>"ideal_answer"</span>:{' '}
              <span className='text-blue-400'>ideal_answer</span>
              <br />
              &nbsp;&nbsp;&#125;
              <br />
              )
              <br />
              <span className='text-purple-600'>print</span>(<span className='text-green-400'>"Response:"</span>,{' '}
              <span className='text-blue-400'>response</span>.<span className='text-blue-400'>content</span>)
            </pre>
          </div>
        </div>
        {/* API Key */}
        <div>
          <p className='mb-2'>Create an API Token and run your eval</p>
          {!showModal && (
            <button className='bg-white text-black border border-black px-4 py-2 rounded font-medium' onClick={requestNewToken}>
              Create API Token
            </button>
          )}
          {showModal && (
            <div>
              <p className='text-sm text-gray-500'>
                This is your new API token. Please copy it now. For security reasons, it won't be displayed again.
              </p>
              <div style={{ display: 'flex', gap: 10 }}>
                <input
                  readOnly
                  value={newToken}
                  className='mt-3 text-center w-full px-3 py-2 text-gray-700 border rounded-lg focus:outline-none'
                />
                <button
                  id='ok-btn'
                  onClick={copyTokenToClipboard}
                  className='mt-3 px-3 py-2 bg-white text-black border border-black text-base font-medium rounded-md shadow-sm focus:outline-none focus:ring-2'
                >
                  Copy
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default GettingStarted;